import { useEffect, useState } from "react";
import { ScrollText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Card, EmptyState } from "@/components/ranger/PageHeader";
import { StatusBadge } from "@/components/ranger/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtTime } from "@/lib/api";

const AuditTable = ({ rows }) => {
  if (!rows.length) return <EmptyState icon={ScrollText} title="No audit records" testId="audit-empty">Administrative actions, logins and RCON commands are recorded here.</EmptyState>;
  return (
    <div className="overflow-x-auto">
      <Table data-testid="audit-log-table">
        <TableHeader><TableRow className="border-[#1E2D4A]">{["Time", "Actor", "Action", "Target", "Result", "Details"].map((h) => <TableHead key={h} className="rg-label">{h}</TableHead>)}</TableRow></TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} className="border-[#1E2D4A]/60 font-mono text-xs">
              <TableCell className="whitespace-nowrap text-slate-500">{fmtTime(r.created_at)}</TableCell>
              <TableCell className="text-slate-300">{r.actor_name}</TableCell>
              <TableCell className="text-[#E5202E]">{r.action}</TableCell>
              <TableCell className="text-slate-400">{r.target || "—"}</TableCell>
              <TableCell><StatusBadge state={r.result} /></TableCell>
              <TableCell className="max-w-xs truncate text-slate-500" title={JSON.stringify(r.details)}>{Object.keys(r.details).length ? JSON.stringify(r.details) : "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default function AuditLogs() {
  const { active, can, user } = useAuth();
  const [scope, setScope] = useState("community");
  const [filter, setFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    const url = scope === "system" ? "/audit/system" : `/communities/${active.id}/audit${filter ? `?action=${encodeURIComponent(filter)}` : ""}`;
    setError("");
    api.get(url).then((r) => setRows(r.data)).catch((e) => { setRows([]); setError(errMsg(e)); });
  }, [active.id, scope, filter]);

  if (!can("moderator")) return <EmptyState title="Restricted">Moderator role required to view audit logs.</EmptyState>;
  const tab = (id, label) => <button data-testid={`audit-scope-${id}`} onClick={() => setScope(id)} className={`rounded-sm px-3 py-1.5 text-xs font-semibold uppercase tracking-wider ${scope === id ? "bg-[#E5202E]/15 text-[#E5202E]" : "text-slate-400 hover:text-slate-200"}`}>{label}</button>;

  return (
    <div>
      <PageHeader eyebrow="Accountability" title="Audit Logs" description="Immutable record of every administrative action in this community." />
      <Card right={
        <div className="flex flex-wrap items-center gap-2">
          {tab("community", active.name)}
          {user.is_owner && tab("system", "System / logins")}
          {scope === "community" && <Input data-testid="audit-filter-input" placeholder="Filter: rcon, staff, console…" value={filter} onChange={(e) => setFilter(e.target.value)} className="h-8 w-56 border-[#1E2D4A] bg-[#050811] font-mono text-xs" />}
        </div>
      }>
        {error ? <p className="text-sm text-red-400">{error}</p> : <AuditTable rows={rows} />}
      </Card>
    </div>
  );
}
